import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MATTR_SMS_NUMBER, SMS_LINK, getApiBaseUrl } from '../../config/api';
import brandonPhoto from '../../assets/brandon-cape-town.jpg';
import '../../css/ChicagoPage.css';

/**
 * The campaign landing page (mattr.coach/chicago).
 *
 * This is the destination for the Instagram bio link and any Achilles-campaign
 * post. Unlike the sunlit /joke page, it is opened indoors on a phone from a
 * social bio, so it wears the dark brand aesthetic and does one job: convert a
 * warm follower into a CHICAGO text (a paying signup whose first month funds the
 * Achilles fundraiser).
 *
 * Eager-imported in index.js (like /joke, unlike every lazy route below it): a
 * bio tap is always a cold direct load in Instagram's in-app browser, where a
 * lazy chunk would open the page on the "Loading..." fallback. ~2KB is worth the
 * round trip saved on the exact traffic this page exists to convert.
 */

// `?&body=` is the cross-platform form: iOS wants `&body=`, Android wants
// `?body=`. The body is what fires `chicago_signup_keyword` attribution in
// sms_service.py CTA_KEYWORDS, so do not remove it. As with /joke, the onboarding
// service returns the consent disclosure WITHOUT processing the body, so a
// prefilled CHICAGO is safe here (nothing is pending; the stranger initiates).
const CHICAGO_SMS_LINK = `${SMS_LINK}?&body=CHICAGO`;

// Achilles' OWN fundraising page (same URL the landing page links). Donations go
// straight to the 501(c)(3); the founder never touches the money, so this is a
// clean outbound link, not a donate-into-the-paid-funnel path. It exists so a
// non-runner supporter (the bulk of a personal-account audience) has a way to
// help without buying coaching: the difference between the campaign reading as
// "raising money for them" vs "using them to sell subscriptions".
const ACHILLES_FUNDRAISER_URL =
  'https://give.achillesinternational.org/fundraiser/7341264';

/** +19179949725 -> (917) 994-9725. Derived so the number has one source of truth. */
const formatSmsNumber = (e164) => {
  const digits = (e164 || '').replace(/\D/g, '').replace(/^1/, '');
  if (digits.length !== 10) return e164;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const track = (event, params) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', event, params);
  }
};

function ChicagoPage() {
  const [stats, setStats] = useState({
    total: null,
    goal: null,
    athletes: null,
    workouts: null
  });

  useEffect(() => {
    document.title = 'Chicago Marathon fundraiser · mattr.coach';
  }, []);

  // The global body rule (style.css) is `display:flex; justify-content:center;
  // align-items:center; height:100vh`, which vertically CENTERS this whole page and
  // clips its top off-screen on a short phone viewport (the landing page overrides
  // the same rule in JS). Neutralize it only while this page is mounted so tall
  // content flows and scrolls from the top. Cleanup restores it for other routes.
  useEffect(() => {
    document.body.classList.add('chicago-active');
    return () => document.body.classList.remove('chicago-active');
  }, []);

  // Live stats (donation + social proof). Same endpoint the landing page reads;
  // fail silently to no-stats (never block the CTA on a hiccup). The backend
  // applies its own honesty floor, so a field is null when it shouldn't be shown.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${getApiBaseUrl()}/api/clients/landing-page/stats`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setStats({
          total: data.donation_total ?? null,
          goal: data.donation_goal ?? null,
          athletes: data.athlete_count ?? null,
          workouts: data.workouts_completed ?? null
        });
      })
      .catch(() => {
        /* no stats on failure (CTA still works) */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasDonation =
    stats.total != null && stats.goal != null && stats.goal > 0;
  const pct = hasDonation
    ? Math.max(0, Math.min(100, (stats.total / stats.goal) * 100))
    : 0;
  const hasAthletes = stats.athletes != null;
  const hasWorkouts = stats.workouts != null;

  return (
    <main className="chicago-page">
      <div className="chicago-card">
        <p className="chicago-eyebrow">Chicago Marathon · Achilles International</p>

        <h1 className="chicago-headline">Run your race. Help me run mine.</h1>

        <p className="chicago-sub">
          I&apos;m running the Chicago Marathon for Achilles International. Your first
          month funds my fundraiser, and you get your own AI running coach to text
          anytime on top of it.
        </p>

        {/* Athletes/workouts sit right next to the fundraiser meter: real training
            usage (credibility) beside the money it raises ties the two together.
            Both render only when the stats fetch returns. */}
        {(hasAthletes || hasWorkouts) && (
          <div className="chicago-stats">
            {hasAthletes && (
              <div className="chicago-stat">
                <span className="chicago-stat-num" aria-hidden="true">
                  {stats.athletes.toLocaleString()}
                </span>
                <span className="chicago-stat-label" aria-hidden="true">
                  athletes training
                </span>
                <span className="sr-only">
                  {stats.athletes.toLocaleString()} athletes training with Mattr
                </span>
              </div>
            )}
            {hasAthletes && hasWorkouts && (
              <span className="chicago-stat-divider" aria-hidden="true" />
            )}
            {hasWorkouts && (
              <div className="chicago-stat">
                <span className="chicago-stat-num" aria-hidden="true">
                  {stats.workouts.toLocaleString()}
                </span>
                <span className="chicago-stat-label" aria-hidden="true">
                  workouts logged
                </span>
                <span className="sr-only">
                  {stats.workouts.toLocaleString()} workouts logged
                </span>
              </div>
            )}
          </div>
        )}

        {/* The fundraiser meter IS the campaign, so it sits high (right under the
            intro) where it can't be missed. Renders once the stats fetch returns
            donation data; degrades to nothing on a fetch miss. */}
        {hasDonation && (
          <div
            className="chicago-ticker"
            role="img"
            aria-label={`$${stats.total.toLocaleString()} of $${stats.goal.toLocaleString()} raised for my Achilles fundraiser`}
          >
            <div className="chicago-ticker-nums" aria-hidden="true">
              <span className="chicago-ticker-amount">
                ${stats.total.toLocaleString()}
              </span>
              <span className="chicago-ticker-goal">
                of ${stats.goal.toLocaleString()} raised
              </span>
            </div>
            <div className="chicago-ticker-bar" aria-hidden="true">
              <span
                className="chicago-ticker-fill"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {/* DONATE IS PRIMARY (changed 2026-08-16), and the audience decided it.
            Over the first two weeks live: chicago_donate_tap = 8 users,
            chicago_cta_tap = 1 user — and that 1 was the founder testing. Zero
            strangers have ever texted CHICAGO. This page is reached from a personal
            Instagram bio, so its traffic is people who came to support a friend's
            marathon, not runners shopping for a coach.

            The old layout made the coach the primary ask and kept donate as a quiet
            self-selecting text link, deliberately, to avoid cannibalizing signups.
            That reasoning no longer holds: there are no signups here to cannibalize,
            and the campaign has a hard deadline (Oct 1 / race day Oct 11) with a real
            gap left to close. Burying the only action this audience actually takes
            under one they never take costs money for nothing.

            The self-selecting hedge ("Just here to support?") is gone with it —
            hedging a PRIMARY ask just weakens it. Runners are still served by the
            secondary button immediately below.

            KEEP the `chicago_donate_tap` / `chicago_cta_tap` event names exactly as
            they are, or the before/after comparison this change exists to test is
            destroyed. */}
        <a
          className="chicago-donate-btn"
          href={ACHILLES_FUNDRAISER_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track('chicago_donate_tap', {})}
        >
          Donate to my Achilles fundraiser
          <span className="chicago-donate-arrow" aria-hidden="true">&rarr;</span>
        </a>

        {/* Coach is now the SECONDARY path: still a real button (never demoted to a
            text link the way donate used to be), just outlined instead of filled, so
            a runner who lands here has an obvious route without outcompeting the ask
            that actually converts. "Or" makes the either/or explicit.

            The label carries the FUNDING motive because that is what this audience
            responds to (8:1 donate over coach), but it must never call this a
            donation or name a price:
              - It is a SUBSCRIPTION, not a gift. You receive a service, nothing is
                tax-deductible, and it auto-renews monthly.
              - "$15" is wrong three ways: the price is $14.99, the user pays $0
                today (14-day trial), and it recurs rather than being one-time.
              - The fine print ~100px below this button already reads "$0 today,
                cancel anytime. $14.99/mo after a 14-day free trial." A button
                promising a quick $15 donation would contradict the page's own
                disclosure within one scroll, and contradict the Stripe checkout
                the tap leads to.
            NAME THE BENEFICIARY. An earlier label read "and fund my run", which
            failed twice: the money does not fund the run (Matthew pays his own
            entry and travel; donations go to Achilles against his $3,000
            commitment), and with no beneficiary named it reads as asking a
            stranger to underwrite a hobby. "Support my run for Achilles" is
            unremarkable in charity fundraising precisely BECAUSE the cause is
            named; drop the cause and only the "my" is left. Keep Achilles in the
            label.

            Price stays in fine print, deliberately, so it is not a deterrent at
            the button. */}
        <a
          className="chicago-cta-btn is-secondary"
          href={CHICAGO_SMS_LINK}
          onClick={() => track('chicago_cta_tap', {})}
        >
          {/* Chat-bubble glyph signals "this opens a message" pre-lexically, before
              the label is read — resolves the tap-vs-text confusion. currentColor
              inherits the button's text color. */}
          <svg className="chicago-cta-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
          Or get your own run coach and support Achilles
        </a>

        {/* Sets the SMS mechanic BEFORE they tap: the deep link opens Messages with
            CHICAGO pre-filled, and the click-to-text drop-off is people not hitting
            send. Tell them to send + what happens next so they don't stall in the
            Messages app (where the page can no longer instruct them). Stays directly
            under the coach button it describes. */}
        <p className="chicago-how">
          Opens a pre-filled text. Just <strong>hit send</strong>, and your coach
          texts you back to build your plan.
        </p>

        {/* Photo BELOW the CTA + disclaimers so the ask (CTA + hit-send + price) sits
            above the fold right under the ticker (no figure named here on purpose —
            the seed is a manual sync point that moves, and it has already outrun this
            comment twice: $382 -> $432 -> $1,007 -> $1,227 -> $1,842 -> $2,147 -> $2,217). The photo is
            the first reward
            on scroll, its top peeking at the fold. */}
        <figure className="chicago-proof">
          <img
            className="chicago-proof-img"
            src={brandonPhoto}
            width="300"
            height="300"
            alt="Matthew and a fellow guide with Brandon, all wearing finisher medals, at the Cape Town Marathon"
          />
          <figcaption className="chicago-proof-cap">
            Guiding Brandon (center), the world&apos;s youngest autistic athlete to
            finish all 8 Marathon Majors.
          </figcaption>
        </figure>

        <ul className="chicago-value">
          <li>
            <span className="chicago-value-mark" aria-hidden="true">✓</span>
            An AI coach that texts back in seconds, day or night
          </li>
          <li>
            <span className="chicago-value-mark" aria-hidden="true">✓</span>
            A plan that adapts to your real week, not a static PDF
          </li>
          <li>
            <span className="chicago-value-mark" aria-hidden="true">✓</span>
            Built on real coaching science, from a NASM-trained runner
          </li>
        </ul>

        {/* Pricing moved off the CTA to fine print: still disclosed (and it surfaces
            again in SMS onboarding + at checkout) but it doesn't tax the decision at
            the button, where the ask + Achilles framing are already a lot to parse. */}
        <p className="chicago-reassure">
          <strong>$0 today, cancel anytime.</strong> $14.99/mo after a 14-day free
          trial.
        </p>

        {/* Desktop fallback: sms: is inert there, so the number must be readable
            text and CHICAGO must be typeable (it is the sole attribution trigger). */}
        <p className="chicago-fallback">
          Doesn&apos;t open? Text <strong>CHICAGO</strong> to{' '}
          <strong>{formatSmsNumber(MATTR_SMS_NUMBER)}</strong>
        </p>

        {/* CTIA point-of-CTA disclosure. Same content as /joke; frequency MUST
            match CONSENT_DISCLOSURE_TEXT in sms_onboarding_service.py. Legible and
            adjacent to the CTA, never behind a tap. */}
        <p className="chicago-legal">
          Coaching texts. Msg frequency varies, usually a text or two on training
          days. Msg &amp; data rates may apply. Reply STOP to cancel, HELP for help.{' '}
          <a href="/terms">Terms</a> ·{' '}
          <a href="/privacy">Privacy</a>
        </p>
      </div>

      <a
        className="chicago-wordmark"
        href="/"
        onClick={() => track('chicago_wordmark_tap', {})}
      >
        mattr.coach
      </a>
    </main>
  );
}

export default ChicagoPage;
